import { useContext, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  ColumnData,
  HandleLoginMap,
  OrderStatus,
  OrderStatusCode,
  PatientOrdersTableData,
} from '../interface/interface';
import { useQuery } from '@tanstack/react-query';
import instance from '../config/axios';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import saveAs from 'file-saver';
import { Table } from '../components/Table';
import { PaginationState } from '@tanstack/react-table';
import { stringify } from 'qs';
import { DownloadInvoiceIcon, OrderSummaryIcon } from '../assets/svgImages';
import Accordion from '../components/Accordion';
import moment from 'moment';
import { UserContext } from '../context/userContext';
import loading from '../assets/loading.svg';
import { nativeService } from '../helpers/nativeService';
import { NativeResponseType } from '../interface/native.interface';

const OrdersTable = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { setLoadingEnable, nativeView } = useContext(UserContext);

  const [buttonLoadingEnable, setButtonLoadingEnable] =
    useState<boolean>(false);

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const handleToggle = (index: number) => {
    if (openIndex === index) {
      setOpenIndex(null);
    } else {
      setOpenIndex(index);
    }
  };

  const [state, setState] = useState<HandleLoginMap<'ResultMap'>>({
    showResult: false,
    data: [],
    error: null,
  });

  // Pagination
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [totalRecords, setTotalRecordCount] = useState<number>(0);

  // Search
  const [searchString, setSearchString] = useState<string>('');
  const [submitSearch, setSubmission] = useState<number>(0);
  const [tableData, setTableData] = useState<Array<PatientOrdersTableData>>([]);

  const onDownload = async (id: number, code: string) => {
    setButtonLoadingEnable(true);
    if (!id) {
      console.error(`Order ID passed as undefined!`);
    }

    try {
      const token = user?.access_token;

      const response = await instance.get(`/api/v1/order/${id}/invoice`, {
        headers: {
          'Content-Type': 'application/pdf',
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      if (nativeView) {
        const reader = new FileReader();
        reader.onloadend = function () {
          let base64data = '';
          if (typeof reader.result === 'string') {
            base64data = reader.result.split(',')[1];
          }
          nativeService(
            JSON.stringify({
              type: NativeResponseType.DOWNLOAD_BLOB,
              filename: `${code}_Invoice.pdf`,
              base64: base64data,
            }),
          );
        };
        reader.readAsDataURL(response?.data);
        return;
      }

      saveAs(response.data, `${code}_Invoice.pdf`);
    } catch (error) {
      console.error('File download failed:', error);
    } finally {
      setButtonLoadingEnable(false);
    }
  };

  const getOrders = async () => {
    try {
      const response = await instance(
        `/api/v1/orders?${stringify({ limit: pagination.pageSize, page: pagination.pageIndex, search: searchString })}`,
        {
          headers: {
            Authorization: `Bearer ${user?.access_token}`,
          },
        },
      );

      const responseData = response.data;
      setState({
        ...state,
        showResult: true,
        data: responseData.data,
      });
      setTotalRecordCount(response.data.total);

      return true;
    } catch (error: any) {
      setState({
        ...state,
        error: error.error,
      });
      return false;
    }
  };

  useEffect(() => {
    if (state.error || !state.data) return;

    setTableData(() => {
      return state.data?.map(
        (d) =>
          ({
            id: d.id,
            orderStatus: d.orderStatus,
            invoiceId: d.invoiceId,
            createdDate: d.createdDate,
            totalItems: d.orderProductCount,
            paidAmount: d.totalAmount,
            downloadInvoice: {
              orderCode: d.orderCode,
              orderId: d.id,
              orderStatus: d.orderStatus.id,
            },
            viewOrder: { id: d.id },
          }) as PatientOrdersTableData,
      );
    });
  }, [state]);

  const { isFetching } = useQuery({
    queryKey: ['orders', submitSearch, pagination],
    queryFn: getOrders,
  });

  useEffect(() => {
    if (!isFetching) {
      setLoadingEnable(false);
    } else {
      setLoadingEnable(true);
    }
  }, [isFetching]);

  if (isFetching) {
    return <Loading />;
  }

  // Table Specific Props
  const columns: Array<ColumnData> = [
    {
      key: 'createdDate',
      header: 'Date',
      cell: (info) => {
        const date = moment(info.getValue());
        return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : '-';
      },
    },
    {
      key: 'invoiceId',
      header: 'Order ID',
      cell: (info) => info.getValue(),
    },
    {
      key: 'totalItems',
      header: 'Total Items',
      cell: (info) => info.getValue(),
    },
    {
      key: 'paidAmount',
      header: 'Amount Paid',
      cell: (info) => `${info.getValue()}`,
    },
    {
      key: 'orderStatus',
      header: 'Order Status',
      cell: (info) => {
        return (
          <>
            <span
              className="w-2 h-2 mr-2 rounded-full inline-block"
              style={{ backgroundColor: info.getValue().colorCode }}
            ></span>
            <span>
              {[OrderStatus.PAUSED, OrderStatus.AMENDING_DISPENSE].includes(
                info.getValue().name,
              )
                ? 'In Progress'
                : info.getValue().name}
            </span>
          </>
        );
      },
    },
    {
      key: 'downloadInvoice',
      header: 'Download Invoice',
      cell: (info) => {
        const value = info.getValue();
        return (
          <button
            type="button"
            className="block rounded-md bg-transparent px-3 py-2 text-center disabled:cursor-not-allowed disabled:opacity-50  ml-8"
            onClick={() => onDownload(value?.orderId, value?.orderCode)}
            disabled={value?.orderStatus < OrderStatusCode.DISPENSED}
          >
            <DownloadInvoiceIcon />
          </button>
        );
      },
    },
    {
      key: 'viewOrder',
      header: 'View Order',
      cell: (info) => {
        const value = info.getValue();
        return (
          <button
            type="button"
            className="block ml-8"
            onClick={() => navigate(`/order-summary-patient/${value.id}`)}
          >
            <OrderSummaryIcon />
          </button>
        );
      },
    },
  ];
  if (state?.data?.length < 1) {
    return <p className="text-center text-lg font-semibold">No order found!</p>;
  }

  return (
    <div>
      <div className="hidden xl:block">
        <div className="flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden sm:rounded-lg">
                <Table
                  columns={columns}
                  data={tableData}
                  enableSorting={false}
                  enablePagination
                  enableSearch
                  pagintation={{
                    state: pagination,
                    setter: setPagination,
                    totalRecords: totalRecords,
                  }}
                  search={{
                    value: searchString,
                    changeHandler: setSearchString,
                    submitSearch: setSubmission,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="block xl:hidden">
        <div className="my-4 hidden">
          <input
            type="text"
            className="border-0 bg-secondary-cornflowerblue-50 rounded-md px-4 py-3 text-primary-blue-500 text-sm font-regular w-full"
            placeholder="Search something here"
          />
        </div>
        <div className="p-4 bg-secondary-pink-50 rounded-md">
          {state.data?.map((d, index) => (
            <>
              <Accordion
                title={
                  <div className="flex flex-col">
                    <p className="text-xs text-primary-blue-500 font-regular mb-1">
                      {moment(d.createdDate).isValid()
                        ? moment(d.createdDate).utc().format('MMM DD, YYYY')
                        : '-'}
                    </p>
                    <p className="text-sm text-primary-blue-500 font-semibold mb-3">
                      ORDER ID
                      <span className="text-secondary-pink-600">
                        {' '}
                        #{d.invoiceId}
                      </span>
                    </p>
                    <p className="text-sm text-primary-blue-500 font-semibold mb-1">
                      Total Item(s) : {d.orderProductCount}
                    </p>
                    {/* <p className="text-xs text-primary-blue-500 font-regular">
                  + <span>2</span> more medications
                </p> */}
                  </div>
                }
                isOpen={openIndex === index}
                onAccordionToggle={() => handleToggle(index)}
              >
                <ul>
                  <li className="rounded-md bg-secondary-cornflowerblue-50 p-4 flex justify-between items-center mb-2">
                    <p className="text-sm text-primary-blue-500 font-semibold">
                      Amount Paid
                    </p>
                    <p className="text-sm text-primary-blue-500 font-semibold flex items-center">
                      {d.totalAmount}
                    </p>
                  </li>
                  <li className="rounded-md bg-secondary-cornflowerblue-50 p-4 flex justify-between items-center mb-3">
                    <p className="text-sm text-primary-blue-500 font-semibold">
                      Order Status
                    </p>
                    <p className="text-sm text-primary-blue-500 font-semibold flex items-center">
                      <span
                        className="w-2.5 h-2.5  mr-2 rounded-full"
                        style={{
                          backgroundColor: `${d.orderStatus.colorCode}`,
                        }}
                      ></span>
                      <span>{d.orderStatus.name}</span>
                    </p>
                  </li>
                  <li className="flex justify-center mb-4">
                    <button
                      className="border-0 bg-transparent text-primary-blue-500 font-semibold font-base flex items-center mr-3"
                      onClick={() => onDownload(d?.id, d?.orderCode)}
                      disabled={d.orderStatus.id < OrderStatusCode.DISPENSED}
                    >
                      <span>Download Invoice </span>
                      {buttonLoadingEnable ? (
                        <>
                          <div className="col-span-2 xl:col-span-1 text-end p-1">
                            <img
                              height={'20px'}
                              width={'20px'}
                              src={loading}
                              alt="Loading"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <DownloadInvoiceIcon className="ml-3" />
                        </>
                      )}
                    </button>
                  </li>
                  <li className="flex justify-center">
                    <button
                      className="bg-transparent border-0 text-sm text-primary-blue-500 font-regular underline"
                      onClick={() => navigate(`/order-summary-patient/${d.id}`)}
                    >
                      View Order
                    </button>
                  </li>
                </ul>
              </Accordion>
            </>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrdersTable;
